import { useState } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Tabs,
  Tab,
  Container,
  Box,
  Paper,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import CategoryIcon from "@mui/icons-material/Category";
import PeopleIcon from "@mui/icons-material/People";
import EventIcon from "@mui/icons-material/Event";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import HistoryIcon from "@mui/icons-material/History";
import HomeIcon from "@mui/icons-material/Home";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PersonIcon from "@mui/icons-material/Person";
import MenuIcon from "@mui/icons-material/Menu";
import ThunderstormIcon from "@mui/icons-material/Thunderstorm";
import FeedbackIcon from "@mui/icons-material/Feedback";
import AssignmentLateIcon from "@mui/icons-material/AssignmentLate";
import InventoryTypes from "./pages/InventoryTypes";
import InventoryItems from "./pages/InventoryItems";
import StockHistory from "./pages/StockHistory";
import StockReport from "./pages/StockReport";
import Personnel from "./pages/Personnel";
import PersonnelSync from "./pages/PersonnelSync";
import Events from "./pages/Events";
import Incidents from "./pages/Incidents";
import Compliance from "./pages/Compliance";
import Audit from "./pages/Audit";
import Feedback from "./pages/Feedback";
import ClinicalReports from "./pages/ClinicalReports";
import Guidelines from "./pages/Guidelines";
import GuidelinesView from "./pages/GuidelinesView";
import MyDetails from "./pages/MyDetails";
import MyEvents from "./pages/MyEvents";
import SafetyForecastPage from "./safety/SafetyForecastPage";
import UserMenu from "./components/UserMenu";
import Notifier from "./components/Notifier";
import ViewAsBanner from "./components/ViewAsBanner";
import ViewAsPicker from "./components/ViewAsPicker";
import { AUTH_ENABLED } from "./authConfig";
import { useMe } from "./MeContext";

// The route to land on when switching into each view.
const HOME_ROUTE = { admin: "/", user: "/my-details" };
const LOGO = process.env.PUBLIC_URL + "/ShelterStoneLogo.png";

function Home() {
  return (
    <Paper elevation={0} sx={{ p: 5, textAlign: "center", border: "1px solid", borderColor: "divider" }}>
      <Box component="img" src={LOGO} alt="Shelter Stone" sx={{ maxWidth: 420, width: "100%", mb: 2 }} />
      <Typography color="text.secondary">
        Track inventory, personnel and incidents for events with Shelter Stone. Use the tabs above to get started.
      </Typography>
    </Paper>
  );
}

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { me, loading, can } = useMe();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // "admin" shows the management tabs the user's global roles allow; "user" shows
  // a personal view. The choice persists across sessions.
  const [storedMode, setStoredMode] = useState(
    () => (localStorage.getItem("viewMode") === "user" ? "user" : "admin"));
  const year = new Date().getFullYear();

  // The admin tabs a user can see are driven by their global permissions; the
  // toggle only appears for someone with an admin side to switch to.
  const hasAdmin = can("AdminToggle.view");
  const viewMode = hasAdmin ? storedMode : "user";

  // The personal pages are open to everyone; Guidelines is permissioned like any
  // other page, but its content isn't tied to one event — so it asks whether *any*
  // of the user's event roles grants it.
  const userTabs = [
    { label: "My details", value: "/my-details", icon: <PersonIcon />, element: <MyDetails />, show: true },
    { label: "My events", value: "/my-events", icon: <EventIcon />, element: <MyEvents />, show: true },
    { label: "Incidents", value: "/incidents", icon: <ReportProblemIcon />, element: <Incidents userMode />, show: true },
    { label: "Guidelines", value: "/guidelines", icon: <MenuBookIcon />, element: <GuidelinesView />,
      show: can("Guidelines.viewLive", { anyEvent: true }) },
  ].filter(t => t.show);
  const adminTabs = [
    { label: "Home", value: "/", icon: <HomeIcon />, element: <Home />, show: true },
    { label: "Inventory", value: "/inventory-items", icon: <Inventory2Icon />, element: <InventoryItems />, show: can("InventoryItems.view") },
    // Reached from a button on the Inventory page rather than the nav — types are set
    // up once and then mostly left alone, so they don't warrant a tab of their own.
    { label: "Types", value: "/inventory-types", icon: <CategoryIcon />, element: <InventoryTypes />,
      show: can("InventoryTypes.view"), hidden: true },
    // Batch-tracked stock (Drugs, Medical) — the ledger and the report, both reached
    // from buttons on the Inventory page, often with a filter already set.
    { label: "Stock history", value: "/stock-history", icon: <HistoryIcon />, element: <StockHistory />,
      show: can("Stock.viewHistory"), hidden: true },
    { label: "Stock report", value: "/stock-report", icon: <Inventory2Icon />, element: <StockReport />,
      show: can("Stock.viewHistory"), hidden: true },
    { label: "Personnel", value: "/personnel", icon: <PeopleIcon />, element: <Personnel />, show: can("Personnel.viewAll") },
    // The roster against MailChimp and O365, from a button on the Personnel page.
    { label: "MailChimp & O365 check", value: "/personnel-sync", icon: <PeopleIcon />, element: <PersonnelSync />,
      show: can("Personnel.syncCheck"), hidden: true },
    { label: "Events", value: "/events", icon: <EventIcon />, element: <Events />, show: can("Events.view") },
    { label: "Incidents", value: "/incidents", icon: <ReportProblemIcon />, element: <Incidents />, show: can("Incidents.view") },
    { label: "Compliance", value: "/compliance", icon: <FactCheckIcon />, element: <Compliance />, show: can("Compliance.view") },
    { label: "Feedback", value: "/feedback", icon: <FeedbackIcon />, element: <Feedback />, show: can("Feedback.view") },
    { label: "Clinical reports", value: "/clinical-reports", icon: <AssignmentLateIcon />, element: <ClinicalReports />, show: can("ClinicalReports.view") },
    { label: "Guidelines", value: "/guidelines", icon: <MenuBookIcon />, element: <Guidelines />, show: can("Guidelines.view") },
    { label: "Audit", value: "/audit", icon: <HistoryIcon />, element: <Audit />, show: can("AuditLog.view") },
    // Weather safety assessments — beta, Super Admin + Events Manager (src/safety/).
    { label: "Safety", value: "/safety", icon: <ThunderstormIcon />, element: <SafetyForecastPage />,
      show: can("SafetyForecast.use") },
  ].filter(t => t.show);

  // `tabs` drives the routes; `navTabs` drives the nav strip/drawer. A hidden tab is
  // routable but not offered — on a hidden route no tab shows as selected.
  const tabs = viewMode === "user" ? userTabs : adminTabs;
  const navTabs = tabs.filter(t => !t.hidden);
  const navValues = navTabs.map(t => t.value);
  const current = navValues.includes(location.pathname) ? location.pathname : false;
  const fallback = navTabs[0]?.value || "/my-details";

  const go = (value) => { navigate(value); setDrawerOpen(false); };

  const switchView = (_, mode) => {
    if (!mode || mode === viewMode) return; // ToggleButtonGroup yields null when re-clicking the active button
    localStorage.setItem("viewMode", mode);
    setStoredMode(mode);
    navigate(HOME_ROUTE[mode]);
    setDrawerOpen(false);
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "background.default" }}>
      <AppBar
        position="static"
        elevation={0}
        sx={{ bgcolor: "background.paper", color: "text.primary", borderBottom: "1px solid", borderColor: "divider" }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <Box
            component="img"
            src={LOGO}
            alt="Shelter Stone"
            sx={{ height: 44, mr: 2, cursor: "pointer" }}
            onClick={() => navigate(HOME_ROUTE[viewMode])}
          />
          {isMobile ? (
            // Spacer keeps the hamburger pinned to the right of the toolbar.
            <Box sx={{ flexGrow: 1 }} />
          ) : (
            // Grow to fill the space between logo and the right-hand controls, and
            // let the tab strip scroll (rather than clip/hide tabs) when there
            // isn't room for them all before the mobile breakpoint collapses it.
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Tabs value={current} onChange={(_, v) => navigate(v)}
                    textColor="primary" indicatorColor="primary"
                    variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
                {navTabs.map(item => (
                  <Tab key={item.value} icon={item.icon} iconPosition="start"
                       label={item.label} value={item.value} />
                ))}
              </Tabs>
            </Box>
          )}
          {isMobile && (
            <IconButton edge="end" color="inherit" aria-label="Open navigation menu"
                        onClick={() => setDrawerOpen(true)}>
              <MenuIcon />
            </IconButton>
          )}
          {hasAdmin && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={viewMode}
              onChange={switchView}
              aria-label="View mode"
              sx={{ ml: isMobile ? 0 : 2 }}
            >
              <ToggleButton value="admin" aria-label="Admin view">Admin</ToggleButton>
              <ToggleButton value="user" aria-label="User view">User</ToggleButton>
            </ToggleButtonGroup>
          )}
          {me?.is_super && !me?.impersonating && <ViewAsPicker />}
          {AUTH_ENABLED && <UserMenu />}
        </Toolbar>
      </AppBar>

      <ViewAsBanner />

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 240 }} role="presentation">
          <List>
            {navTabs.map(item => (
              <ListItemButton key={item.value} selected={current === item.value}
                              onClick={() => go(item.value)}>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Drawer>

      <Container maxWidth="lg" sx={{ py: 4, flexGrow: 1 }}>
        <Routes>
          {tabs.map(t => <Route key={t.value} path={t.value} element={t.element} />)}
          <Route path="*" element={<Navigate to={fallback} replace />} />
        </Routes>
      </Container>

      <Box
        component="footer"
        sx={{
          py: 2,
          textAlign: "center",
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          © Shelter Stone {year}
        </Typography>
      </Box>

      <Notifier />
    </Box>
  );
}
